import type { IReportTable } from '~/presentation/reports/common/type';
import type { ReactNode } from 'react';

import { useFetchReport, useReportOrderTotal } from '../hooks';
import { createContext, useContext, useMemo } from 'react';

export interface ReportProviderProps {
  children: ReactNode;
}

export interface IReportContextType {
  tableData: IReportTable;
  isLoading?: boolean;
  totalStoreOrders?: number;
  refetch: () => void;
}

export const ReportContext = createContext<IReportContextType | undefined>(undefined);

export const ReportProvider: React.FC<ReportProviderProps> = ({ children }) => {
  const { data, isLoading, refetch } = useFetchReport();
  const { totalStoreOrders } = useReportOrderTotal();

  const tableData = useMemo(() => {
    if (!data?.tableData || !data.tableData.columns || !data.tableData.rows)
      return { columns: [], rows: [] };
    return data.tableData as IReportTable;
  }, [data]);

  const contextValue = useMemo(
    () => ({
      tableData,
      isLoading,
      totalStoreOrders,
      refetch,
    }),
    [tableData, isLoading, totalStoreOrders, refetch],
  );
  return <ReportContext.Provider value={contextValue}>{children}</ReportContext.Provider>;
};

export const useReportContext = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }
  return context;
};
